// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { PositionSummaryItem } from '@client/banks/components/shared/positions-list';
import type { BankPosition } from '@client/banks/types/position';
import { useBankUploadData } from '@client/banks/providers/bank-upload-provider';
import { formatAccountIbanReference } from '@client/banks/utils/account-reference';
import { getBankPositionAssetTypeLabel } from '@client/banks/utils/bank-position-asset-type-label';
import { hasBankUploadListData } from '@client/banks/utils/has-bank-upload-list-data';
import { formatPositionInvestmentValue } from '@client/banks/utils/position-valuation';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import produce from 'immer';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const usePositionsList = () => {
  const { t } = useTranslation();
  const { navigate } = useStepsData();
  const { formData, saveFormData, openListEditor } = useBankUploadData();

  const canFinalize = useMemo(() => hasBankUploadListData(formData), [formData]);

  const positions = useMemo(() => formData.positions ?? [], [formData.positions]);
  const isEmpty = useMemo(() => positions.length === 0, [positions.length]);

  const getAssetTypeLabel = useCallback(
    (assetType: BankPosition['assetType']) =>
      getBankPositionAssetTypeLabel(assetType, t),
    [t],
  );

  const items = useMemo<PositionSummaryItem[]>(
    () =>
      positions.map((position, index) => {
        const meta = [
          getAssetTypeLabel(position.assetType),
          formatAccountIbanReference(position.iban),
        ].join(' · ');

        return {
          index,
          position,
          meta,
          investmentLabel: formatPositionInvestmentValue(position),
        };
      }),
    [getAssetTypeLabel, positions],
  );

  const openPositionForm = useCallback(
    (index?: number) => {
      openListEditor('positions', index);
      navigate('positions-form');
    },
    [navigate, openListEditor],
  );

  const onAdd = useCallback(() => {
    openPositionForm();
  }, [openPositionForm]);

  const onImportExcel = useCallback(() => {
    navigate('positions-import');
  }, [navigate]);

  const onEdit = useCallback(
    (index: number) => {
      openPositionForm(index);
    },
    [openPositionForm],
  );

  const onDelete = useCallback(
    (index: number) => {
      saveFormData(
        'positions',
        produce(positions, draft => {
          draft.splice(index, 1);
        }),
      );
    },
    [positions, saveFormData],
  );

  return {
    items,
    isEmpty,
    canFinalize,
    onAdd,
    onImportExcel,
    onEdit,
    onDelete,
  };
};
